import React, { useState, useEffect, useMemo } from 'react';
import { format, isToday, isYesterday, parseISO } from 'date-fns';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { productService, ProductEvent } from '@/services/productService';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectContent,
} from '@/components/ui/select';

import { useProductDetailPageTranslations } from '@/hooks/useProductDetailPageTranslations';
import { formatDateForDisplay } from '@/utils/numberFormat';
import { useOrgSettings } from '@/hooks/useOrgSettings';
import { formatValueForHistory } from '@/utils/textUtils';
import { logError, logDebug } from '@/utils/errorHandler';
import { useCategories } from '@/components/categories/SubcategoryManager/useCategories';
import { Category } from '@/types/categories';

// Utility function to handle backend translation objects
const getTranslatedText = (messageOrObject: any, historyLabels: any): string => {
  // If we received a JSON string (option-2 backend), attempt to parse it
  if (typeof messageOrObject === 'string') {
    const trimmed = messageOrObject.trim();
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      try {
        // Parse the JSON; if it fails we fall back to the raw string
        messageOrObject = JSON.parse(trimmed);
      } catch {
        return messageOrObject; // Return raw if not valid JSON
      }
    } else {
      return messageOrObject;
    }
  }

  // If it's a translation object from backend
  if (
    messageOrObject &&
    typeof messageOrObject === 'object' &&
    (messageOrObject.translationKey || messageOrObject.key)
  ) {
    const { translationKey, key, params } = messageOrObject;

    // Support both old (key) and new (translationKey) formats during transition
    const fullKey = translationKey || (key ? `productdetailpage.tabs.history.${key}` : '');

    // Extract the key path from the translation key
    // e.g., 'productdetailpage.tabs.history.summaries.productCreated' -> 'summaries.productCreated'
    const keyPath = fullKey.replace('productdetailpage.tabs.history.', '');
    const keyParts = keyPath.split('.');

    // Navigate through the historyLabels object
    let result = historyLabels;
    for (const part of keyParts) {
      result = result?.[part];
      if (!result) break;
    }

    // If we found the translation, interpolate parameters
    if (typeof result === 'string' && params) {
      let interpolated = result;
      Object.entries(params).forEach(([key, value]) => {
        // Pass through parameter values as-is without translation
        interpolated = interpolated.replace(new RegExp(`{{${key}}}`, 'g'), String(value));
      });
      return interpolated;
    }

    // If we found a valid translation, return it
    if (typeof result === 'string') {
      return result;
    }

    // Log debug info when translation fails
    logDebug('Translation lookup failed:', {
      translationKey: fullKey,
      keyPath,
      availableKeys: Object.keys(historyLabels),
      result,
      component: 'ProductHistoryTab',
    });

    // Fallback to the original translation key
    return fullKey;
  }

  // Fallback to string representation
  return String(messageOrObject);
};

interface ProductHistoryTabProps {
  productId: number;
}

// Helper function to format change keys for display
const formatKey = (key: string, historyLabels: any, t?: any): string => {
  // Handle specific field mappings using translations
  const fieldMappings: Record<string, string> = {
    is_active: historyLabels.fieldTypes.status,
    family: historyLabels.fieldTypes.family,
    price: historyLabels.fieldTypes.price,
    name: historyLabels.fieldTypes.name,
    sku: historyLabels.fieldTypes.sku,
    description: historyLabels.fieldTypes.description,
    category: historyLabels.fieldTypes.category,
    brand: historyLabels.fieldTypes.brand,
    gtin: historyLabels.fieldTypes.gtin,
    tags: historyLabels.fieldTypes.tags,
    images: historyLabels.fieldTypes.images,
    created_by: historyLabels.fieldTypes.created_by,
    is_archived: historyLabels.fieldTypes.is_archived,
    organization: historyLabels.fieldTypes.organization,
    barcode: historyLabels.fieldTypes.barcode,
  };

  if (fieldMappings[key]) return fieldMappings[key];

  // Handle price-related fields using direct translation
  if (t) {
    if (key === 'amount') {
      return t('productdetailpage.priceTab.table.headers.value') || 'Amount';
    }
    if (key === 'currency') {
      return t('productdetailpage.priceTab.table.headers.currency') || 'Currency';
    }
    if (key === 'price_type' || key === 'priceType') {
      return t('productdetailpage.priceTab.table.headers.priceType') || 'Price Type';
    }
  }

  // Convert camelCase or snake_case to Title Case
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

// Helper function to find category name by ID
const findCategoryNameById = (categories: Category[], categoryId: number): string | null => {
  for (const category of categories) {
    if (category.id === categoryId) {
      return category.name;
    }
    if (category.children) {
      const found = findCategoryNameById(category.children, categoryId);
      if (found) return found;
    }
  }
  return null;
};

// Helper to format values for display
const formatValue = (
  value: any,
  historyLabels: any,
  fieldName?: string,
  categories?: Category[]
): string => {
  if (value === null || value === undefined) return historyLabels.values.none;
  if (typeof value === 'boolean') return value ? historyLabels.values.yes : historyLabels.values.no;
  if (typeof value === 'string' && value.trim() === '') return historyLabels.values.empty;

  // Handle category fields - try to resolve ID to name
  if ((fieldName === 'category' || fieldName === 'value') && categories) {
    // Check if it's a numeric ID
    const categoryId = typeof value === 'string' ? parseInt(value, 10) : value;
    if (!isNaN(categoryId) && typeof categoryId === 'number') {
      const categoryName = findCategoryNameById(categories, categoryId);
      if (categoryName) {
        return categoryName;
      }
    }
  }

  // Use the enhanced formatting utility that handles HTML and tags
  const formattedValue = formatValueForHistory(value, fieldName);
  return formattedValue || historyLabels.values.empty;
};

// Enhanced helper to render changes with better formatting
const ChangesView = ({
  changes,
  historyLabels,
  categories,
  t,
}: {
  changes: Record<string, { old: any; new: any }>;
  historyLabels: any;
  categories?: Category[];
  t?: any;
}) => {
  return (
    <div className="space-y-3">
      {Object.entries(changes).map(([name, change]) => {
        if (name === 'attributes' || name === 'primary_image' || !change) return null;

        // Format attribute keys for display
        let label = formatKey(name, historyLabels, t);
        if (name.startsWith('attribute:')) {
          label = name.replace('attribute:', '').trim();
        }

        const oldValue = formatValue(change.old, historyLabels, name, categories);
        const newValue = formatValue(change.new, historyLabels, name, categories);

        return (
          <div
            key={name}
            className="flex flex-col gap-1 rounded-md bg-muted/30 p-3 dark:bg-muted/20"
          >
            <div className="text-sm font-medium text-muted-foreground">{label}</div>
            <div className="flex items-center gap-2 text-sm">
              <span className="rounded bg-red-50 px-2 py-1 text-red-600 line-through dark:bg-red-900/20 dark:text-red-400">
                {oldValue}
              </span>
              <span className="text-muted-foreground/60">→</span>
              <span className="rounded bg-green-50 px-2 py-1 text-green-600 dark:bg-green-900/20 dark:text-green-400">
                {newValue}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Enhanced helper to render bulk operation details
const BulkOperationView = ({
  data,
  historyLabels,
  categories,
  t,
}: {
  data: Record<string, any>;
  historyLabels: any;
  categories?: Category[];
  t?: any;
}) => {
  const { field, value, bulk_operation } = data;

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20">
        <div className="mb-2 flex items-center gap-2">
          <div className="size-2 rounded-full bg-blue-500"></div>
          <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
            {historyLabels.changeDetails.bulkOperation}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">
              {historyLabels.changeDetails.fieldUpdated}:
            </span>
            <div className="font-medium text-foreground">
              {formatKey(field || historyLabels.values.unknown, historyLabels, t)}
            </div>
          </div>
          <div>
            <span className="text-muted-foreground">{historyLabels.changeDetails.newValue}:</span>
            <div className="font-medium text-foreground">
              {formatValue(value, historyLabels, field, categories)}
            </div>
          </div>
        </div>
        {bulk_operation && (
          <div className="mt-2 text-xs text-muted-foreground">
            {historyLabels.changeDetails.operation}:{' '}
            {bulk_operation === true
              ? historyLabels.changeDetails.bulkUpdate
              : String(bulk_operation)}
          </div>
        )}
      </div>
    </div>
  );
};

// Helper to render old data in a clean format
const OldDataView = ({
  data,
  historyLabels,
  categories,
  t,
}: {
  data: Record<string, any>;
  historyLabels: any;
  categories?: Category[];
  t?: any;
}) => {
  // Filter out internal fields and null values for cleaner display
  const filteredData = Object.entries(data).filter(([key, value]) => {
    return (
      !key.startsWith('_') &&
      !key.includes('id') &&
      value !== null &&
      value !== undefined &&
      key !== 'created_at' &&
      key !== 'updated_at'
    );
  });

  if (filteredData.length === 0) return null;

  return (
    <div className="mt-3 rounded-md border border-border bg-muted/30 p-4 dark:bg-muted/20">
      <div className="mb-3 flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <div className="size-2 rounded-full bg-muted-foreground/60"></div>
        {historyLabels.changeDetails.previousState}
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {filteredData.map(([key, value]) => (
          <div key={key} className="space-y-1">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground/80">
              {formatKey(key, historyLabels, t)}
            </span>
            <div className="rounded border border-border bg-background p-2 text-sm text-foreground">
              {formatValue(value, historyLabels, key, categories)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Helper to render asset information in a clean format
const AssetInfoView = ({
  data,
  historyLabels,
  categories,
  t,
}: {
  data: Record<string, any>;
  historyLabels: any;
  categories?: Category[];
  t?: any;
}) => {
  return (
    <div className="rounded-md border border-purple-200 bg-purple-50 p-4 dark:border-purple-800 dark:bg-purple-900/20">
      <div className="mb-3 flex items-center gap-2 text-sm font-medium text-purple-800 dark:text-purple-200">
        <div className="size-2 rounded-full bg-purple-500"></div>
        {historyLabels.changeDetails.assetInformation}
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {Object.entries(data).map(([key, value]) => (
          <div key={key} className="space-y-1">
            <span className="text-xs font-medium uppercase tracking-wide text-purple-600 dark:text-purple-300">
              {formatKey(key, historyLabels, t)}
            </span>
            <div className="break-words rounded border border-border bg-background p-2 text-sm text-foreground">
              {formatValue(value, historyLabels, key, categories)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Helper to render generic payload data in a structured way
const GenericPayloadView = ({
  data,
  eventType,
  historyLabels,
  categories,
  t,
}: {
  data: Record<string, any>;
  eventType: string;
  historyLabels: any;
  categories?: Category[];
  t?: any;
}) => {
  // Filter out empty or internal fields
  const filteredData = Object.entries(data).filter(([key, value]) => {
    return !key.startsWith('_') && value !== null && value !== undefined && value !== '';
  });

  if (filteredData.length === 0) {
    return (
      <div className="rounded bg-muted/30 p-3 text-sm italic text-muted-foreground dark:bg-muted/20">
        {historyLabels.changeDetails.noAdditionalDetails}
      </div>
    );
  }

  // Choose color scheme based on event type
  const getColorScheme = (type: string) => {
    if (type.includes('created'))
      return {
        bg: 'bg-green-50 dark:bg-green-900/20',
        border: 'border-green-200 dark:border-green-800',
        text: 'text-green-800 dark:text-green-200',
        dot: 'bg-green-500',
      };
    if (type.includes('updated'))
      return {
        bg: 'bg-blue-50 dark:bg-blue-900/20',
        border: 'border-blue-200 dark:border-blue-800',
        text: 'text-blue-800 dark:text-blue-200',
        dot: 'bg-blue-500',
      };
    if (type.includes('deleted') || type.includes('removed'))
      return {
        bg: 'bg-red-50 dark:bg-red-900/20',
        border: 'border-red-200 dark:border-red-800',
        text: 'text-red-800 dark:text-red-200',
        dot: 'bg-red-500',
      };
    return {
      bg: 'bg-slate-50 dark:bg-slate-900',
      border: 'border-slate-200 dark:border-slate-700',
      text: 'text-slate-800 dark:text-slate-200',
      dot: 'bg-slate-500',
    };
  };

  const colors = getColorScheme(eventType);

  return (
    <div className={`rounded-md border p-4 ${colors.bg} ${colors.border}`}>
      <div className={`mb-3 flex items-center gap-2 text-sm font-medium ${colors.text}`}>
        <div className={`size-2 rounded-full ${colors.dot}`}></div>
        {historyLabels.changeDetails.eventDetails}
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {filteredData.map(([key, value]) => (
          <div key={key} className="space-y-1">
            <span
              className={`text-xs font-medium uppercase tracking-wide ${colors.text} opacity-75`}
            >
              {formatKey(key, historyLabels, t)}
            </span>
            <div className="break-words rounded border border-border bg-background p-2 text-sm text-foreground">
              {formatValue(value, historyLabels, key, categories)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

function getEventTypeBadge(eventType: string, historyLabels: any, t?: any) {
  const typeMap: Record<string, { color: string; label: string }> = {
    created: {
      color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      label: historyLabels.eventTypes.created,
    },
    updated: {
      color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
      label: historyLabels.eventTypes.updated,
    },
    bulk_updated: {
      color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
      label: historyLabels.eventTypes.bulkUpdated,
    },
    price_changed: {
      color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
      label: historyLabels.eventTypes.priceChanged,
    },
    family_changed: {
      color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
      label: historyLabels.eventTypes.familyChanged,
    },
    stock_changed: {
      color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
      label: historyLabels.eventTypes.stockChanged,
    },
    category_changed: {
      color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
      label: historyLabels.eventTypes.categoryChanged,
    },
    description_changed: {
      color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
      label: historyLabels.eventTypes.descriptionChanged,
    },
    asset_added: {
      color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
      label: historyLabels.eventTypes.assetAdded,
    },
    asset_removed: {
      color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
      label: historyLabels.eventTypes.assetRemoved,
    },
    activated: {
      color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      label: historyLabels.eventTypes.activated,
    },
    deactivated: {
      color: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
      label: historyLabels.eventTypes.deactivated,
    },
    deleted: {
      color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
      label: historyLabels.eventTypes.deleted,
    },
    bulk_deleted: {
      color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
      label: historyLabels.eventTypes.bulkDeleted,
    },
    price_created: {
      color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      label: historyLabels.eventTypes.priceCreated,
    },
    price_updated: {
      color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
      label: historyLabels.eventTypes.priceUpdated,
    },
    price_deleted: {
      color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
      label: historyLabels.eventTypes.priceDeleted,
    },
    archived: {
      color: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
      label: historyLabels.eventTypes.archived,
    },
    rollback: {
      color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
      label: historyLabels.eventTypes.rollback,
    },
  };

  const style = typeMap[eventType.toLowerCase()] || {
    color: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300',
    label: formatKey(eventType, historyLabels, t),
  };

  return (
    <Badge className={`font-normal ${style.color}`} variant="outline">
      {style.label}
    </Badge>
  );
}

// Enhanced render details function with comprehensive formatting
function renderDetails(event: ProductEvent, historyLabels: any, categories?: Category[], t?: any) {
  if (!event.payload) return null;

  const payload = event.payload as any;

  // Handle change events with "changes" property (highest priority)
  if (payload.changes) {
    return (
      <div className="space-y-4">
        <ChangesView
          changes={payload.changes}
          historyLabels={historyLabels}
          categories={categories}
          t={t}
        />
        {payload.old_data && (
          <OldDataView
            data={payload.old_data}
            historyLabels={historyLabels}
            categories={categories}
            t={t}
          />
        )}
      </div>
    );
  }

  // Handle bulk operations
  if (event.event_type === 'bulk_updated' || payload.bulk_operation || payload.field) {
    return (
      <BulkOperationView
        data={payload}
        historyLabels={historyLabels}
        categories={categories}
        t={t}
      />
    );
  }

  // Handle asset events
  if (event.event_type === 'asset_added' || event.event_type === 'asset_removed') {
    return (
      <AssetInfoView data={payload} historyLabels={historyLabels} categories={categories} t={t} />
    );
  }

  // Handle category changes
  if (event.event_type === 'category_changed' || payload.category !== undefined) {
    return (
      <GenericPayloadView
        data={payload}
        eventType={event.event_type}
        historyLabels={historyLabels}
        categories={categories}
        t={t}
      />
    );
  }

  // Handle any other structured data
  if (typeof payload === 'object' && Object.keys(payload).length > 0) {
    return (
      <GenericPayloadView
        data={payload}
        eventType={event.event_type}
        historyLabels={historyLabels}
        categories={categories}
        t={t}
      />
    );
  }

  // Fallback for truly empty or unstructured data
  return (
    <div className="rounded bg-slate-50 p-3 text-sm italic text-slate-500 dark:bg-slate-800 dark:text-slate-400">
      {historyLabels.changeDetails.noAdditionalDetails}
    </div>
  );
}

function HistoryEventCard({
  event,
  historyLabels,
  dateHelpers,
  categories,
  t,
}: {
  event: ProductEvent;
  historyLabels: any;
  dateHelpers: any;
  categories?: Category[];
  t?: any;
}) {
  return (
    <Card className="flex flex-col gap-4 border border-slate-200 p-6 shadow-sm transition-shadow duration-200 hover:shadow-md dark:border-slate-700">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {getEventTypeBadge(event.event_type, historyLabels, t)}
          <div className="flex items-center gap-2 text-sm">
            <span className="text-slate-600 dark:text-slate-400">{historyLabels.eventCard.by}</span>
            <span className="font-medium text-slate-900 dark:text-slate-100">
              {event.created_by_name || historyLabels.eventCard.system}
            </span>
          </div>
        </div>
        <div className="text-xs font-medium text-slate-500 dark:text-slate-400">
          {dateHelpers.getLocalizedRelativeTimeString(event.created_at)}
        </div>
      </div>

      {/* Event Summary */}
      {event.summary && (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-300">
          {getTranslatedText(event.summary, historyLabels)}
        </div>
      )}

      {/* Event Details */}
      <div className="space-y-3">{renderDetails(event, historyLabels, categories, t)}</div>
    </Card>
  );
}

// Localized DatePickerWithRange component for History tab
function LocalizedDatePickerWithRange({
  date,
  setDate,
  historyLabels,
  className,
}: {
  date: { from: Date | undefined; to: Date | undefined };
  setDate: (date: { from: Date | undefined; to: Date | undefined }) => void;
  historyLabels: any;
  className?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [fromDate, setFromDate] = React.useState<string>('');
  const [toDate, setToDate] = React.useState<string>('');
  const containerRef = React.useRef<HTMLDivElement>(null);
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const { orgSettings } = useOrgSettings();
  const dateFormat = orgSettings?.date_format || 'MDY';

  // Sync internal state with external date prop
  React.useEffect(() => {
    if (date?.from) {
      setFromDate(format(date.from, 'yyyy-MM-dd'));
    } else {
      setFromDate('');
    }

    if (date?.to) {
      setToDate(format(date.to, 'yyyy-MM-dd'));
    } else {
      setToDate('');
    }
  }, [date]);

  // Handle clicks outside the component
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [open]);

  // Format the date for display in the button
  const formatDisplayDate = () => {
    if (date?.from && date?.to) {
      return `${formatDateForDisplay(date.from, dateFormat)} - ${formatDateForDisplay(date.to, dateFormat)}`;
    } else if (date?.from) {
      return `${formatDateForDisplay(date.from, dateFormat)} - ${historyLabels.datePicker.selectEndDate}`;
    }
    return historyLabels.datePicker.pickDateRange;
  };

  // Handle from date change
  const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFromDate(value);

    if (value) {
      const newFromDate = new Date(value);
      const currentToDate = toDate ? new Date(toDate) : undefined;

      // If to date is before from date, clear it
      if (currentToDate && currentToDate < newFromDate) {
        setToDate('');
        setDate({ from: newFromDate, to: undefined });
      } else {
        setDate({ from: newFromDate, to: currentToDate });
      }
    } else {
      setDate({ from: undefined, to: toDate ? new Date(toDate) : undefined });
    }
  };

  // Handle to date change
  const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setToDate(value);

    if (value) {
      const newToDate = new Date(value);
      const currentFromDate = fromDate ? new Date(fromDate) : undefined;

      // If from date is after to date, clear from date
      if (currentFromDate && currentFromDate > newToDate) {
        setFromDate('');
        setDate({ from: undefined, to: newToDate });
      } else {
        setDate({ from: currentFromDate, to: newToDate });

        // Auto-close when both dates are selected
        if (currentFromDate) {
          setTimeout(() => setOpen(false), 100);
        }
      }
    } else {
      setDate({
        from: fromDate ? new Date(fromDate) : undefined,
        to: undefined,
      });
    }
  };

  // Handle clear
  const handleClear = () => {
    setFromDate('');
    setToDate('');
    setDate({ from: undefined, to: undefined });
    setOpen(false);
  };

  // Calculate popover position
  const [popoverStyle, setPopoverStyle] = React.useState<React.CSSProperties>({});

  React.useEffect(() => {
    if (open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const popoverHeight = 200; // Approximate height

      let top = rect.bottom + 8;

      // If popover would go below viewport, show it above
      if (rect.bottom + popoverHeight > viewportHeight) {
        top = rect.top - popoverHeight - 8;
        if (top < 0) {
          top = rect.bottom + 8;
        }
      }

      setPopoverStyle({
        position: 'fixed',
        top: `${top}px`,
        left: `${rect.left}px`,
        zIndex: 9999,
        minWidth: `${rect.width}px`,
      });
    }
  }, [open]);

  return (
    <>
      <div className={`relative ${className}`} ref={containerRef}>
        <Button
          variant="outline"
          className={`w-full justify-start text-left font-normal ${!date?.from ? 'text-muted-foreground' : ''}`}
          onClick={() => setOpen(!open)}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="mr-2 size-4"
          >
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
          {formatDisplayDate()}
        </Button>
      </div>

      {open && (
        <div
          ref={popoverRef}
          className="w-auto rounded-md border bg-white p-4 shadow-lg dark:bg-slate-950"
          style={popoverStyle}
        >
          <div className="space-y-4">
            <div className="mb-3 text-sm font-medium text-slate-700 dark:text-slate-300">
              {historyLabels.datePicker.selectDateRange}
            </div>

            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">
                  {historyLabels.datePicker.fromDate}
                </label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={handleFromDateChange}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                  max={toDate || undefined}
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">
                  {historyLabels.datePicker.toDate}
                </label>
                <input
                  type="date"
                  value={toDate}
                  onChange={handleToDateChange}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                  min={fromDate || undefined}
                />
              </div>
            </div>

            <div className="flex items-center justify-between border-t pt-3">
              <div className="text-xs text-slate-500">
                {fromDate && toDate
                  ? historyLabels.datePicker.rangeSelected
                  : fromDate
                    ? historyLabels.datePicker.selectEndDate
                    : historyLabels.datePicker.selectStartDate}
              </div>
              <Button variant="outline" size="sm" onClick={handleClear}>
                {historyLabels.datePicker.clear}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Localized date/time helper functions
function createLocalizedDateHelpers(locale: string, historyLabels: any) {
  // Get localized relative time string using Intl.RelativeTimeFormat
  const getLocalizedRelativeTimeString = (dateString: string): string => {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInMs = now.getTime() - date.getTime();

      const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

      const diffInSeconds = Math.floor(diffInMs / 1000);
      const diffInMinutes = Math.floor(diffInSeconds / 60);
      const diffInHours = Math.floor(diffInMinutes / 60);
      const diffInDays = Math.floor(diffInHours / 24);

      if (diffInSeconds < 60) {
        return rtf.format(-diffInSeconds, 'second');
      } else if (diffInMinutes < 60) {
        return rtf.format(-diffInMinutes, 'minute');
      } else if (diffInHours < 24) {
        return rtf.format(-diffInHours, 'hour');
      } else if (diffInDays < 30) {
        return rtf.format(-diffInDays, 'day');
      } else {
        // For dates older than 30 days, show formatted date
        return new Intl.DateTimeFormat(locale, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        }).format(date);
      }
    } catch (error) {
      logError(
        'Failed to get relative time for date',
        {
          component: 'ProductHistoryTab',
          action: 'getLocalizedRelativeTimeString',
          metadata: { dateString, error },
        },
        {
          showToast: false, // Don't show toast for date formatting errors
          userFacing: false, // This is a technical issue, not user-facing
        }
      );
      return dateString;
    }
  };

  // Get localized date label (Today/Yesterday/Date)
  const getLocalizedDateLabel = (dateString: string, dateFormat: any): string => {
    const date = parseISO(dateString);
    if (isToday(date)) return historyLabels.dateLabels.today;
    if (isYesterday(date)) return historyLabels.dateLabels.yesterday;
    return formatDateForDisplay(date, dateFormat);
  };

  return {
    getLocalizedRelativeTimeString,
    getLocalizedDateLabel,
  };
}

const ProductHistoryTab: React.FC<ProductHistoryTabProps> = ({ productId }) => {
  const [events, setEvents] = useState<ProductEvent[]>([]);
  const [pageInfo, setPageInfo] = useState({ page: 1, next: true });
  const [loading, setLoading] = useState(true);
  const [changeType, setChangeType] = useState('all');
  const [dateRange, setDateRange] = useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>({ from: undefined, to: undefined });
  const [changedBy, setChangedBy] = useState('all');
  const [showAllAttributes, setShowAllAttributes] = useState<Record<string, boolean>>({});
  const [displayLimit, setDisplayLimit] = useState(10);

  // Get categories for resolving category IDs to names in history
  const { categories } = useCategories();

  // Get translations and organization settings
  const { getHistoryLabels, t, currentLanguage } = useProductDetailPageTranslations();
  const historyLabels = getHistoryLabels;
  const { orgSettings } = useOrgSettings();
  const dateFormat = orgSettings?.date_format || 'MDY';

  // Create localized date helpers using current locale
  const dateHelpers = useMemo(
    () => createLocalizedDateHelpers(currentLanguage, historyLabels),
    [currentLanguage, historyLabels]
  );

  // Remove hardcoded options and fetch event types from the backend
  // We'll track available event types from the API data
  const [eventTypeOptions, setEventTypeOptions] = useState<{ value: string; label: string }[]>([
    { value: 'all', label: historyLabels.filters.all }, // Only include "all" initially as it's not from the backend
  ]);

  const loadEvents = async (page = 1) => {
    if (!productId) {
      logDebug('No productId provided, skipping load', { productId });
      return;
    }

    logDebug('Loading events for product', { productId, page });
    setLoading(true);

    try {
      // Use parameter names exactly as expected by Django backend
      const filters: Record<string, any> = {
        page,
        page_size: 20,
      };

      // Handle field-specific filtering vs generic event type filtering
      let clientSideFieldFilter: string | null = null;

      if (changeType !== 'all') {
        if (changeType.startsWith('field:')) {
          // For field-specific filters, we'll filter client-side
          clientSideFieldFilter = changeType.replace('field:', '');
          // Don't send event_type filter to backend for field-specific filters
        } else {
          // For generic event types, filter on backend
          filters.event_type = changeType;
        }
      }

      // Only add date filters if dates are actually selected
      if (dateRange.from) {
        const fromDate = dateRange.from.toISOString().slice(0, 10);
        filters.date_from = fromDate;
      }

      if (dateRange.to) {
        const toDate = dateRange.to.toISOString().slice(0, 10);
        filters.date_to = toDate;
      }

      // Match created_by parameter in backend - handle ID 0 special case
      if (changedBy !== 'all') {
        if (changedBy === '0') {
          // For System user (ID 0), send null or undefined as created_by
          // depending on what your backend expects
          filters.created_by = null;
        } else {
          // For regular users, send their ID as before
          filters.created_by = Number(changedBy);
        }
      }

      logDebug('API call filters', { filters });

      // Call the service with the filters
      const data = await productService.getProductHistory(productId, page, filters);

      logDebug('API response', { dataCount: data?.results?.length, totalCount: data?.count });

      // Process the results
      const results = data && Array.isArray(data.results) ? data.results : [];

      // Apply client-side field filtering if needed
      let filteredResults = results;
      if (clientSideFieldFilter) {
        filteredResults = results.filter(event => {
          // Check if this event has changes for the specific field
          if (event.event_type === 'updated' && event.payload?.changes) {
            return Object.keys(event.payload.changes).includes(clientSideFieldFilter);
          }

          // Check if this is a bulk update for the specific field
          if (event.event_type === 'bulk_updated' && event.payload?.field) {
            return event.payload.field === clientSideFieldFilter;
          }

          // Check for specific event types
          if (clientSideFieldFilter === 'price' && event.event_type === 'price_changed') {
            return true;
          }

          if (clientSideFieldFilter === 'family' && event.event_type === 'family_changed') {
            return true;
          }

          // Check payload for family or price changes
          if (event.payload) {
            if (
              clientSideFieldFilter === 'family' &&
              (event.payload.family !== undefined || event.payload.family_id !== undefined)
            ) {
              return true;
            }
            if (clientSideFieldFilter === 'price' && event.payload.price !== undefined) {
              return true;
            }
          }

          return false;
        });
      }

      logDebug('Processed results', {
        filteredCount: filteredResults.length,
        originalCount: results.length,
        clientSideFilter: clientSideFieldFilter,
      });

      // Update event type options based on ALL results (not filtered) to show all available options
      if (results.length > 0) {
        const eventTypes = results.map(r => r.event_type);
        const uniqueTypes = [...new Set(eventTypes)];

        // Extract specific field changes for more granular filtering
        const fieldChanges = new Set<string>();

        results.forEach(event => {
          // For updated events, extract changed fields
          if (event.event_type === 'updated' && event.payload?.changes) {
            Object.keys(event.payload.changes).forEach(field => {
              fieldChanges.add(field);
            });
          }

          // For bulk_updated events, extract the field that was updated
          if (event.event_type === 'bulk_updated' && event.payload?.field) {
            fieldChanges.add(event.payload.field);
          }

          // For specific event types, add the corresponding field
          if (event.event_type === 'price_changed') {
            fieldChanges.add('price');
          }

          if (event.event_type === 'family_changed') {
            fieldChanges.add('family');
          }

          // Check payload for family or price changes in other structures
          if (event.payload) {
            if (event.payload.family !== undefined || event.payload.family_id !== undefined) {
              fieldChanges.add('family');
            }
            if (event.payload.price !== undefined) {
              fieldChanges.add('price');
            }
          }
        });

        // Create options for generic event types
        const genericOptions = uniqueTypes.map(type => ({
          value: type,
          label: formatEventTypeLabel(type),
        }));

        // Create options for specific field changes
        const fieldOptions = Array.from(fieldChanges)
          .sort() // Sort field names alphabetically
          .map(field => ({
            value: `field:${field}`,
            label: `${formatKey(field, historyLabels, t)} Changes`,
          }));

        // Combine all options
        const newOptions = [
          { value: 'all', label: historyLabels.filters.all },
          ...genericOptions,
          ...fieldOptions,
        ];

        if (newOptions.length > 1) {
          setEventTypeOptions(prev => {
            // Merge without duplicates
            const existing = new Set(prev.map(o => o.value));
            const toAdd = newOptions.filter(o => !existing.has(o.value));
            return [...prev, ...toAdd];
          });
        }
      }

      // Update state with new events
      if (page === 1) {
        logDebug('Setting new events (page 1)', { eventCount: filteredResults.length });
        setEvents(filteredResults);
      } else {
        logDebug('Appending events', { page, eventCount: filteredResults.length });
        setEvents(prev => [...prev, ...filteredResults]);
      }

      setPageInfo({ page, next: !!(data && data.next) });

      logDebug('Updated state', {
        eventsCount: filteredResults.length,
        hasNext: !!(data && data.next),
      });
    } catch (error) {
      logError(
        'Failed to load product history',
        {
          component: 'ProductHistoryTab',
          action: 'loadEvents',
          metadata: { productId, page, error },
        },
        {
          showToast: true,
          userFacing: true,
        }
      );

      // Set empty state on error
      setEvents([]);
      setPageInfo({ page: 1, next: false });
    } finally {
      setLoading(false);
    }
  };

  // Helper to format event type labels from database values
  const formatEventTypeLabel = (eventType: string): string => {
    // Convert snake_case to Title Case
    return eventType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Initial load effect - only run when productId changes
  useEffect(() => {
    logDebug('Initial load effect triggered', { productId });
    if (productId) {
      loadEvents(1);
    }
  }, [productId]); // Only run on productId changes

  // Filter change effect - run when filters change
  useEffect(() => {
    logDebug('Filter change effect triggered', {
      changeType,
      changedBy,
      dateFrom: dateRange.from?.toISOString().slice(0, 10),
      dateTo: dateRange.to?.toISOString().slice(0, 10),
    });
    if (productId) {
      // Reset to page 1 and clear existing events when filters change
      setPageInfo({ page: 1, next: true });
      setEvents([]);
      loadEvents(1);
    }
  }, [
    changeType,
    changedBy,
    // Use stringified versions of dates to ensure dependencies change properly
    dateRange.from?.toISOString().slice(0, 10),
    dateRange.to?.toISOString().slice(0, 10),
  ]); // Explicit dependencies for all filters

  // Extract unique users for filter
  const users = useMemo(() => {
    const map = new Map<string, string>();

    events.forEach(ev => {
      // Store as string keys for consistency
      // Skip if created_by is null/undefined, use special '0' key for System
      const userId =
        ev.created_by !== null && ev.created_by !== undefined ? String(ev.created_by) : '0';

      map.set(userId, ev.created_by_name || historyLabels.eventCard.system);
    });

    const userOptions = Array.from(map.entries()).map(([id, name]) => ({
      value: id,
      label: name,
    }));

    return userOptions;
  }, [events, historyLabels]);

  // Group events by day
  const grouped = useMemo(() => {
    const groups: Record<string, ProductEvent[]> = {};
    events.forEach(ev => {
      const date = format(parseISO(ev.created_at), 'yyyy-MM-dd');
      if (!groups[date]) groups[date] = [];
      groups[date].push(ev);
    });
    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
  }, [events]);

  const handleFilterChange = (filter: string, value: any) => {
    // Reset page to 1 when filters change
    if (filter === 'changeType') {
      setChangeType(value);
    } else if (filter === 'changedBy') {
      setChangedBy(value);

      // If we get a user ID of 0, we need to handle it specially as some backends
      // might need null or a special value for "System" actions
      if (value === '0') {
      }
    } else if (filter === 'dateRange') {
      setDateRange({
        from: value?.from || dateRange.from,
        to: value?.to || dateRange.to,
      });
    }
    // Filters have changed, so we should reset page info
    setPageInfo({ page: 1, next: true });
    setEvents([]);
  };

  // Improve the resetAllFilters function to work on first click
  const resetAllFilters = () => {
    // Define default date range (empty)
    const defaultDateRange = {
      from: undefined,
      to: undefined,
    };

    // Update all the filter states
    setChangeType('all');
    setDateRange(defaultDateRange);
    setChangedBy('all');

    // Show loading state
    setLoading(true);

    // Reset the events list and pagination
    setEvents([]);
    setPageInfo({ page: 1, next: true });

    // Build minimal default filters
    const defaultFilters = {
      page: 1,
      page_size: 20,
    };

    // Call the API immediately with default filters
    productService
      .getProductHistory(productId, 1, defaultFilters)
      .then(data => {
        const results = data && Array.isArray(data.results) ? data.results : [];
        setEvents(results);
        setPageInfo({ page: 1, next: !!(data && data.next) });
        setLoading(false);
      })
      .catch(error => {
        logError(
          'Failed to reset filters',
          {
            component: 'ProductHistoryTab',
            action: 'resetAllFilters',
            metadata: { productId, error },
          },
          {
            showToast: true,
            userFacing: true,
          }
        );
        setLoading(false);
      });
  };

  if (loading && events.length === 0) {
    return (
      <div className="space-y-6">
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filter Bar - Always visible */}
      <div className="mb-4 flex flex-wrap items-end gap-4 rounded-lg border border-border bg-muted/30 p-4 dark:bg-muted/20">
        <div>
          <label className="mb-1 block text-xs font-medium">
            {historyLabels.filters.changeType}
          </label>
          <Select
            value={changeType}
            onValueChange={value => handleFilterChange('changeType', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder={historyLabels.filters.selectChangeType} />
            </SelectTrigger>
            <SelectContent>
              {eventTypeOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">
            {historyLabels.filters.dateRange}
          </label>
          <LocalizedDatePickerWithRange
            date={{ from: dateRange.from, to: dateRange.to }}
            setDate={d => setDateRange(d)}
            historyLabels={historyLabels}
            className="w-[280px]"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">
            {historyLabels.filters.changedBy}
          </label>
          <Select value={changedBy} onValueChange={value => handleFilterChange('changedBy', value)}>
            <SelectTrigger>
              <SelectValue placeholder={historyLabels.filters.selectUser} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem key="all" value="all">
                {historyLabels.filters.all}
              </SelectItem>
              {users.map(u => (
                <SelectItem key={u.value} value={u.value}>
                  {u.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto flex gap-2 self-end">
          <Button variant="outline" onClick={resetAllFilters}>
            {historyLabels.filters.resetFilters}
          </Button>
        </div>
      </div>

      {/* Content Area */}
      {loading && events.length === 0 ? (
        <div className="space-y-6">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : events.length === 0 ? (
        <Card className="p-8 text-center">
          <div className="flex flex-col items-center gap-4">
            <div className="rounded-full bg-muted/50 p-3 dark:bg-muted/30">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-muted-foreground"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M8 12h8"></path>
              </svg>
            </div>
            <div>
              <h3 className="mb-1 text-lg font-medium">{historyLabels.emptyState.title}</h3>
              <p className="text-muted-foreground">
                {historyLabels.emptyState.description}
                <Button onClick={resetAllFilters} variant="link" className="h-auto px-1">
                  {historyLabels.emptyState.resetFiltersLink}
                </Button>
                {historyLabels.emptyState.resetFiltersLinkEnd}
              </p>
            </div>
          </div>
        </Card>
      ) : (
        <ol role="feed" className="space-y-10">
          {grouped.slice(0, displayLimit).map(([date, groupEvents]) => {
            // Group attribute_updated events
            const attrEvents = groupEvents.filter(ev => ev.event_type === 'attribute_updated');
            const otherEvents = groupEvents.filter(ev => ev.event_type !== 'attribute_updated');
            const showAll = showAllAttributes[date];
            const attrToShow = showAll ? attrEvents : attrEvents.slice(0, 10);
            return (
              <li key={date}>
                <div className="mb-2 text-lg font-semibold text-foreground">
                  {dateHelpers.getLocalizedDateLabel(date, dateFormat)}
                </div>
                <ol className="space-y-6">
                  {otherEvents.map(ev => (
                    <HistoryEventCard
                      key={ev.id}
                      event={ev}
                      historyLabels={historyLabels}
                      dateHelpers={dateHelpers}
                      categories={categories}
                      t={t}
                    />
                  ))}
                  {attrToShow.map(ev => (
                    <HistoryEventCard
                      key={ev.id}
                      event={ev}
                      historyLabels={historyLabels}
                      dateHelpers={dateHelpers}
                      categories={categories}
                      t={t}
                    />
                  ))}
                  {attrEvents.length > 10 && !showAll && (
                    <Button
                      variant="ghost"
                      className="mt-2"
                      onClick={() => setShowAllAttributes(s => ({ ...s, [date]: true }))}
                    >
                      {historyLabels.pagination.showMoreAttributes.replace(
                        '{{count}}',
                        String(attrEvents.length - 10)
                      )}
                    </Button>
                  )}
                </ol>
              </li>
            );
          })}
        </ol>
      )}

      {/* View More Button and Pagination - Only show when there are events */}
      {events.length > 0 && (
        <div className="flex justify-center gap-4">
          {grouped.length > displayLimit && (
            <Button variant="outline" onClick={() => setDisplayLimit(prev => prev + 10)}>
              {historyLabels.pagination.viewMoreDays}
            </Button>
          )}

          {pageInfo.next && (
            <Button
              variant="outline"
              onClick={() => loadEvents(pageInfo.page + 1)}
              disabled={loading}
            >
              {loading
                ? historyLabels.pagination.loadingMore
                : historyLabels.pagination.loadMoreFromServer}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductHistoryTab;
